import React from 'react';

import { Icon } from '@edx/paragon';
import { Launch } from '@edx/paragon/icons';
import { useIntl } from 'gatsby-plugin-react-intl';
import { isEdxProgram } from '@prospectus/common/ui/ProductCard/utils';
import {
  externalLinkIconCopy,
  cardFooterCopy,
} from '@prospectus/common/ui/ProductCard/messages';
import {
  CourseOrProgramType,
  QuickFactProps,
} from '@prospectus/common/ui/ProductCard/types';

interface ProductCardFooterProps {
  factoid?: string;
  quickFacts?: QuickFactProps[];
  courseLength?: number;
  footerMessage?: string;
  cardType?: CourseOrProgramType;
  is2UDegreeProgram?: boolean;
}

function ProductCardFooter({
  factoid,
  quickFacts,
  courseLength,
  footerMessage,
  cardType,
  is2UDegreeProgram,
}: ProductCardFooterProps) {
  const intl = useIntl();

  if (footerMessage) {
    return (
      <div className="footer-message d-flex align-items-center">
        <p className="x-small">{footerMessage}</p>
        <Icon
          src={Launch}
          className="ml-1"
          style={{ height: '16px', width: '16px' }}
          screenReaderText={intl.formatMessage(
            externalLinkIconCopy[
              'prospectus.2u-product-card.launch-icon.sr-text'
            ],
          )}
        />
      </div>
    );
  }

  if (courseLength) {
    const courseLengthLabel = courseLength > 1 ? 'Courses' : 'Course';
    return (
      <p className="x-small">
        {intl.formatMessage(
          cardFooterCopy[
            'prospectus.2u-product-card.footer-text.number-of-courses'
          ],
          { length: courseLength, label: courseLengthLabel },
        )}
      </p>
    );
  }

  if (isEdxProgram({ cardType, is2UDegreeProgram })) {
    if (quickFacts && quickFacts.length > 0) {
      const quickFactsCount = quickFacts.length;
      if (quickFactsCount < 2) {
        throw new Error(
          'Unhandled quickFacts case in DiscoveryProgramCard.jsx',
        );
      }
      /* quickFacts 4 items:
        $9,900 tuition idx: 0
        11 courses idx: 1
        Fully online - not in the design, skip it. Idx: 2
        1-3 years Idx: 3
      */

      /* quickFacts 3 items:
        6 Modules idx: 0
        Fully Online - not in the design, skip it. Idx: 1
        2-3 years idx: 2
      */
      const threeFactsArrangement = [1, 3, 0];
      const twoFactsArrangement = [0, 2];
      return (
        <>
          {(quickFactsCount > 3 ? threeFactsArrangement : twoFactsArrangement)
            .map((index) => quickFacts[index])
            .filter(Boolean)
            .map((fact, idx) => (
              <p key={fact.text} className="d-inline-block x-small">
                {idx > 0 && <span className="p-2">•</span>}
                {fact && fact.text}
              </p>
            ))}
        </>
      );
    }
  }

  if (factoid) {
    return <p className="x-small">{factoid}</p>;
  }

  return null;
}

ProductCardFooter.defaultProps = {
  cardType: '',
  factoid: '',
  footerMessage: '',
  quickFacts: [],
  courseLength: undefined,
  is2UDegreeProgram: false,
};

export default ProductCardFooter;
